<template>
  <div>
    <div
      class="c-input-overlay"
      :class="{
        'c-input-overlay_active': container,
        'c-input-overlay_active_select_mobile': type === 'select',
      }"
      @click="closeContainer"
    ></div>

    <div class="c-input">
      <div v-if="label" class="c-input-label">
        {{ label }}
      </div>

      <div v-if="floatingLabel" class="c-input-floating-label-not-visible">
        {{ floatingLabel }}
      </div>

      <div class="c-input-wrapper">
        <div
          v-if="icon"
          class="c-input-icon"
          :class="{
            'c-input-icon_right': iconRight,
            'c-input-icon_size_small': size === 'small',
          }"
        >
          <svg
            v-if="icon === 'search'"
            class="c-input-icon-svg c-input-icon-svg_stroke"
            width="17"
            height="17"
            viewBox="0 0 17 17"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.4142 11L15.6569 15.2426M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>

          <svg
            v-if="icon === 'calendar'"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15.2222 3H2.77778C1.79594 3 1 3.69645 1 4.55556V15.4444C1 16.3036 1.79594 17 2.77778 17H15.2222C16.2041 17 17 16.3036 17 15.4444V4.55556C17 3.69645 16.2041 3 15.2222 3Z"
              stroke="#8E9CBB"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path d="M13 1V5" stroke="#8E9CBB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M5 1V5" stroke="#8E9CBB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <rect x="4" y="8" width="2" height="2" fill="#8E9CBB" />
            <rect x="4" y="12" width="2" height="2" fill="#8E9CBB" />
            <rect x="8" y="8" width="2" height="2" fill="#8E9CBB" />
            <rect x="8" y="12" width="2" height="2" fill="#8E9CBB" />
            <rect x="12" y="8" width="2" height="2" fill="#8E9CBB" />
            <rect x="12" y="12" width="2" height="2" fill="#8E9CBB" />
          </svg>
        </div>

        <div
          v-if="type === 'select' || (type === 'date' && !editable) || type === 'period'"
          class="c-input-value"
          :class="{
            'c-input-value_type_select': type === 'select',
            'c-input-value_placeholder': !selectValue,
            'c-input-value_type_select_clearable': clearable,
            'c-input-value_type_select_multiple': multiple,
            'c-input-value_size_small': size === 'small',
          }"
          @click="openContainer"
        >
          <div v-if="!multiple && type === 'select'" class="c-input-value-single">
            {{ selectValue ? selectValue.label : placeholder }}
          </div>

          <div v-if="multiple" class="c-input-value-multiple">
            <div v-if="!selectValue || selectValue.length === 0" class="c-input-value-placeholder">
              {{ placeholder }}
            </div>

            <div
              v-for="(selectValueItem, selectValueItemIndex) in selectValue"
              :key="selectValueItem.value"
              class="c-input-value-multiple-item"
            >
              <div class="c-input-value-multiple-item-text">
                {{ selectValueItem.label }}
              </div>
              <div class="c-input-value-multiple-item-close">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  xmlns="http://www.w3.org/2000/svg"
                  @click="(e) => dropItem(e, selectValueItemIndex)"
                >
                  <path
                    d="M0.325608 10.746C-0.0649162 10.3555 -0.0649162 9.72233 0.325608 9.33181L9.36312 0.292893C9.75364 -0.0976313 10.3868 -0.0976309 10.7773 0.292893C11.1679 0.683418 11.1679 1.31658 10.7773 1.70711L1.73982 10.746C1.3493 11.1365 0.716132 11.1365 0.325608 10.746Z"
                  />
                  <path
                    d="M10.6836 10.6835C11.0741 10.293 11.0741 9.65986 10.6836 9.26933L1.70711 0.292893C1.31658 -0.0976313 0.683417 -0.0976309 0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L9.26935 10.6835C9.65987 11.0741 10.293 11.0741 10.6836 10.6835Z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div
            v-if="(type === 'date' && !editable) || type === 'period'"
            class="c-input-value-date"
            :class="{
              'c-input-value-date_placeholder': !dateValue,
              'c-input-value-date_size_small': size === 'small',
            }"
          >
            {{ dateValue ? dateValue : placeholder }}
          </div>
        </div>

        <input
          v-if="(!type || type === 'text') && !mask"
          v-model="textValue"
          class="c-input-input"
          :class="{
            'c-input-input_placeholder_italic': placeholderStyle === 'italic',
            'c-input-input_size_small': size === 'small',
            'c-input-input_floating_label_active': textValue && floatingLabel,
          }"
          :maxlength="Number(maxLength)"
          :disabled="type === 'select'"
          :type="type === undefined ? 'text' : 'text'"
          :placeholder="placeholder"
          @focus="focused = true"
          @focusout="focused = false"
        />

        <input
          v-if="mask"
          v-model="textValue"
          v-mask="mask"
          class="c-input-input"
          :class="{
            'c-input-input_placeholder_italic': placeholderStyle === 'italic',
            'c-input-input_size_small': size === 'small',
          }"
          :disabled="type === 'select'"
          :type="type === undefined ? 'text' : 'text'"
          :placeholder="placeholder"
          @focus="focused = true"
          @focusout="focused = false"
        />

        <textarea
          v-if="type === 'textarea'"
          ref="textarea"
          v-model="textValue"
          class="c-input-textarea"
          :class="{
            // 'c-input-input_placeholder_italic': placeholderStyle === 'italic',
            // 'c-input-input_size_small': size === 'small',
            'c-input-textarea_floating_label_active': textValue && floatingLabel,
          }"
          rows="1"
          :maxlength="maxLength"
          :placeholder="placeholder"
          @focus="focused = true"
          @focusout="focused = false"
        >
        </textarea>

        <div
          v-if="floatingLabel"
          class="c-input-floating-label"
          :class="{
            'c-input-floating-label-active':
              (isArray(selectValue) && selectValue.length !== 0) || (!isArray(selectValue) && selectValue),
            'c-input-floating-label-pr': !multiple && !container,
          }"
        >
          {{ floatingLabel }}
        </div>

        <input
          v-if="type === 'date' && editable"
          v-model="textValue"
          v-mask="'##.##.####'"
          class="c-input-input c-input-input_date"
          :class="{
            'c-input-input_placeholder_italic': placeholderStyle === 'italic',
            'c-input-input_size_small': size === 'small',
          }"
          placeholder="00.00.0000"
          @focus="focused = true"
          @focusout="focused = false"
        />

        <div
          v-if="!checkbox || type === 'date' || type === undefined"
          class="c-input-actions"
          :class="{
            'c-input-actions_size_small': size === 'small',
          }"
        >
          <div
            v-if="clearable"
            class="c-input-action c-input-action_clear"
            :class="{
              'c-input-action_clear_active': selectValue || dateValue || textValue,
            }"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg" @click="clear">
              <path
                d="M0.325608 10.746C-0.0649162 10.3555 -0.0649162 9.72233 0.325608 9.33181L9.36312 0.292893C9.75364 -0.0976313 10.3868 -0.0976309 10.7773 0.292893C11.1679 0.683418 11.1679 1.31658 10.7773 1.70711L1.73982 10.746C1.3493 11.1365 0.716132 11.1365 0.325608 10.746Z"
              />
              <path
                d="M10.6836 10.6835C11.0741 10.293 11.0741 9.65986 10.6836 9.26933L1.70711 0.292893C1.31658 -0.0976313 0.683417 -0.0976309 0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L9.26935 10.6835C9.65987 11.0741 10.293 11.0741 10.6836 10.6835Z"
              />
            </svg>
          </div>

          <div
            v-if="type === 'select' && !multiple"
            class="c-input-action c-input-action_arrow"
            :class="{ 'c-input-action_arrow_active': container }"
          >
            <svg
              class="c-input-action-arrow"
              width="12"
              height="7"
              viewBox="0 0 12 7"
              xmlns="http://www.w3.org/2000/svg"
              @click="openContainer"
            >
              <path
                d="M5.30964 6.70905C4.92189 6.32111 4.92189 5.69215 5.30964 5.30421L10.305 0.306472C10.6928 -0.0814626 11.3214 -0.081462 11.7092 0.306473C12.0969 0.694408 12.0969 1.32337 11.7092 1.71131L6.71381 6.70905C6.32606 7.09698 5.69739 7.09698 5.30964 6.70905Z"
              />
              <path
                d="M6.70587 6.70905C7.09363 6.32111 7.09363 5.69215 6.70587 5.30421L1.69498 0.290951C1.30723 -0.096984 0.678565 -0.0969834 0.290814 0.290951C-0.0969378 0.678886 -0.0969378 1.30785 0.290814 1.69579L5.3017 6.70905C5.68946 7.09698 6.31812 7.09698 6.70587 6.70905Z"
              />
            </svg>
          </div>
        </div>

        <div v-if="checkbox" class="c-input-checkbox">
          <c-switcher
            v-model="checkboxValueLocal"
            :label="{
              text: checkbox.text ? checkbox.text : checkbox,
              color: checkbox.labelColor ? checkbox.labelColor : null,
            }"
            type="checkbox"
            :nowrap="true"
          />
        </div>
      </div>

      <div
        class="c-input-border xx"
        :class="{
          'c-input-border_active': focused || container,
          'c-input-border_required': required,
        }"
      ></div>

      <div
        v-if="required"
        class="c-input-required"
        :class="{
          'c-input-required_active':
            type === 'select' || type === 'text' || type === 'textarea' || !type ? textValue === '' : false,
        }"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="7" cy="7" r="6.5" fill="white" stroke="#FF238D" />
          <circle cx="7" cy="10" r="1" fill="#FF238D" />
          <rect x="6" y="3" width="2" height="5" rx="1" fill="#FF238D" />
        </svg>

        <div class="c-input-required-text">{{ required }}</div>
      </div>

      <div
        v-if="type === 'select' || type === 'date' || type === 'period'"
        class="c-input-container"
        :class="{
          'c-input-container_active': container || focused,
          'c-input-container_date': type === 'date',
          'c-input-container_period': type === 'period',
          'c-input-container_size_small': size === 'small',
          'c-input-container_nolabel': !label && !floatingLabel,
        }"
      >
        <div v-if="type === 'period'" class="c-input-container-close" @click="container = false">
          <svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10.6045 11.5377C10.2154 11.9267 9.58272 11.9247 9.19126 11.5333L3.53712 5.88051C3.14565 5.48904 3.14367 4.85633 3.5327 4.4673C3.92172 4.07828 4.55443 4.08026 4.9459 4.47173L10.6 10.1245C10.9915 10.5159 10.9935 11.1486 10.6045 11.5377Z"
              fill="#8E9CBB"
            />
            <path
              d="M10.6127 4.47053C10.2212 4.07907 9.58849 4.07709 9.19947 4.46611L3.53718 10.1284C3.14816 10.5174 3.15014 11.1501 3.54161 11.5416C3.93307 11.9331 4.56579 11.9351 4.95481 11.546L10.6171 5.88374C11.0061 5.49471 11.0041 4.862 10.6127 4.47053Z"
              fill="#8E9CBB"
            />
          </svg>
        </div>

        <div
          v-if="searchable"
          class="c-input-container-search"
          :class="{
            'c-input-container-search-with-special-value': withSpecialValue,
          }"
        >
          <div class="c-input-container-search-icon">
            <svg width="13" height="13" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M11.4142 11L15.6569 15.2426M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </div>

          <input v-model="selectSearch" class="c-input-container-search-input" type="text" placeholder="Поиск" />

          <svg
            v-if="selectSearch.length > 0"
            fill="#FF238D"
            class="c-input-container-search-clear-icon"
            width="12"
            height="12"
            viewBox="0 0 12 12"
            xmlns="http://www.w3.org/2000/svg"
            @click="selectSearch = ''"
          >
            <path
              d="M0.325608 10.746C-0.0649162 10.3555 -0.0649162 9.72233 0.325608 9.33181L9.36312 0.292893C9.75364 -0.0976313 10.3868 -0.0976309 10.7773 0.292893C11.1679 0.683418 11.1679 1.31658 10.7773 1.70711L1.73982 10.746C1.3493 11.1365 0.716132 11.1365 0.325608 10.746Z"
            ></path>
            <path
              d="M10.6836 10.6835C11.0741 10.293 11.0741 9.65986 10.6836 9.26933L1.70711 0.292893C1.31658 -0.0976313 0.683417 -0.0976309 0.292893 0.292893C-0.0976311 0.683418 -0.0976311 1.31658 0.292893 1.70711L9.26935 10.6835C9.65987 11.0741 10.293 11.0741 10.6836 10.6835Z"
            ></path>
          </svg>

          <div class="c-input-container-search-border"></div>
        </div>

        <div v-if="type === 'select'" class="c-input-container-list">
          <div v-if="selectSearchEmpty" class="c-input-container-list-item c-input-container-list-item_empty">
            Совпадений не найдено
          </div>
          <div
            v-for="item in selectItems"
            :key="item.value"
            class="c-input-container-list-item"
            :class="{
              'c-input-container-list-item_disabled': isItemDisabled(item),
              'c-input-container-list-item_selected':
                item !== null && selectValue !== null ? item.value === selectValue.value : false,
              'c-input-container-list-item_with_avatar': item.avatar,
              'c-input-container-list-item_special': item.specialValue !== undefined && item.specialValue,
            }"
            @click="(e) => selectItem(e, item)"
          >
            <div
              v-if="item.avatar"
              class="c-input-container-list-item-avatar"
              :style="{ backgroundImage: `url(${item.avatar})` }"
            ></div>

            <div
              class="c-input-container-list-item-label"
              :class="{
                'c-input-container-list-item-label-bold': item.specialValue !== undefined && item.specialValue,
              }"
            >
              {{ item.label }}
            </div>
            <div
              v-if="item.specialValue !== undefined && item.specialValue"
              class="c-input-container-list-item-border"
            ></div>
          </div>
        </div>

        <div v-if="type === 'select'" class="c-input-container-list-mobile-buttons">
          <c-button
            class="c-input-container-list-mobile-buttons-cancel"
            size="small"
            label="Отмена"
            @click="closeContainer"
          />
          <c-button v-if="multiple" size="small" label="Готово" color="primary" />
        </div>

        <div v-if="type === 'date'" class="c-input-container-date">
          <datepicker
            v-model="selectedDate"
            :inline="true"
            :language="ru"
            :full-month-name="true"
            :monday-first="true"
            calendar-class="c-input-container-calendar"
          />
        </div>

        <div v-if="type === 'period'" class="c-input-container-period">
          <div class="c-input-container-period-actions-mobile">
            <div class="c-input-container-period-actions-mobile-item" @click="periodSubmit">Сбросить</div>
            <div class="c-input-container-period-actions-mobile-item" @click="periodReset">Применить</div>
          </div>

          <div
            class="c-input-container-period-calendars"
            :class="{
              'c-input-container-period-calendars_disabled': period.range !== 'custom',
            }"
          >
            <div
              class="c-input-container-period-calendars-item"
              :class="{
                'c-input-container-period-calendars-item_active': period.activeCalendar === 'start',
              }"
            >
              <div class="c-input-container-period-input-wrapper">
                <the-mask
                  v-model="period.start.input"
                  mask="##.##.####"
                  class="c-input-container-period-input"
                  type="text"
                  placeholder="00.00.0000"
                  :masked="true"
                />

                <div class="c-input-container-period-input-icon">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M15.2222 3H2.77778C1.79594 3 1 3.69645 1 4.55556V15.4444C1 16.3036 1.79594 17 2.77778 17H15.2222C16.2041 17 17 16.3036 17 15.4444V4.55556C17 3.69645 16.2041 3 15.2222 3Z"
                      stroke="#8E9CBB"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13 1V5"
                      stroke="#8E9CBB"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path d="M5 1V5" stroke="#8E9CBB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <rect x="4" y="8" width="2" height="2" fill="#8E9CBB" />
                    <rect x="4" y="12" width="2" height="2" fill="#8E9CBB" />
                    <rect x="8" y="8" width="2" height="2" fill="#8E9CBB" />
                    <rect x="8" y="12" width="2" height="2" fill="#8E9CBB" />
                    <rect x="12" y="8" width="2" height="2" fill="#8E9CBB" />
                    <rect x="12" y="12" width="2" height="2" fill="#8E9CBB" />
                  </svg>
                </div>
              </div>

              <div class="c-input-container-period-calendar">
                <datepicker
                  v-model="period.start.selected"
                  :inline="true"
                  :language="ru"
                  :full-month-name="true"
                  :monday-first="true"
                  :highlighted="{
                    from: period.start.highlighted,
                    to: period.end.highlighted,
                  }"
                  :open-date="period.startMonth"
                  calendar-class="c-input-container-calendar c-input-container-calendar_period"
                  @changedMonth="changedStartMonth"
                  @selected="periodSelectDay"
                />
              </div>
            </div>

            <div
              class="c-input-container-period-calendars-item"
              :class="{
                'c-input-container-period-calendars-item_active': period.activeCalendar === 'end',
              }"
            >
              <div class="c-input-container-period-input-wrapper">
                <the-mask
                  v-model="period.end.input"
                  mask="##.##.####"
                  class="c-input-container-period-input"
                  type="text"
                  placeholder="00.00.0000"
                  :masked="true"
                />

                <div class="c-input-container-period-input-icon">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M15.2222 3H2.77778C1.79594 3 1 3.69645 1 4.55556V15.4444C1 16.3036 1.79594 17 2.77778 17H15.2222C16.2041 17 17 16.3036 17 15.4444V4.55556C17 3.69645 16.2041 3 15.2222 3Z"
                      stroke="#8E9CBB"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M13 1V5"
                      stroke="#8E9CBB"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path d="M5 1V5" stroke="#8E9CBB" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <rect x="4" y="8" width="2" height="2" fill="#8E9CBB" />
                    <rect x="4" y="12" width="2" height="2" fill="#8E9CBB" />
                    <rect x="8" y="8" width="2" height="2" fill="#8E9CBB" />
                    <rect x="8" y="12" width="2" height="2" fill="#8E9CBB" />
                    <rect x="12" y="8" width="2" height="2" fill="#8E9CBB" />
                    <rect x="12" y="12" width="2" height="2" fill="#8E9CBB" />
                  </svg>
                </div>
              </div>

              <div class="c-input-container-period-calendar">
                <datepicker
                  v-model="period.end.selected"
                  :inline="true"
                  :language="ru"
                  :full-month-name="true"
                  :monday-first="true"
                  :highlighted="{
                    from: period.start.highlighted,
                    to: period.end.highlighted,
                  }"
                  :open-date="period.endMonth"
                  calendar-class="c-input-container-calendar c-input-container-calendar_period"
                  @changedMonth="changedEndMonth"
                  @selected="periodSelectDay"
                />
              </div>
            </div>
          </div>

          <div class="c-input-container-period-switcher">
            <div
              class="c-input-container-period-switcher-item"
              :class="{
                'c-input-container-period-switcher-item_active': period.activeCalendar === 'start',
              }"
              @click="period.activeCalendar = 'start'"
            >
              <span>Начало периода</span>
            </div>

            <div
              class="c-input-container-period-switcher-item"
              :class="{
                'c-input-container-period-switcher-item_active': period.activeCalendar === 'end',
              }"
              @click="period.activeCalendar = 'end'"
            >
              <span>Конец периода</span>
            </div>
          </div>

          <div class="c-input-container-period-right">
            <div class="c-input-container-period-links">
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'yesterday',
                }"
                @click="period.range = 'yesterday'"
              >
                Вчера
              </div>
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'today',
                }"
                @click="period.range = 'today'"
              >
                Сегодня
              </div>
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'begin-week',
                }"
                @click="period.range = 'begin-week'"
              >
                C начала недели
              </div>
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'past-week',
                }"
                @click="period.range = 'past-week'"
              >
                Прошлая неделя
              </div>
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'last-7-days',
                }"
                @click="period.range = 'last-7-days'"
              >
                Последние 7 дней
              </div>
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'current-month',
                }"
                @click="period.range = 'current-month'"
              >
                Текущий месяц
              </div>
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'past-month',
                }"
                @click="period.range = 'past-month'"
              >
                Прошлый месяц
              </div>
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'last-month',
                }"
                @click="period.range = 'last-month'"
              >
                Последний месяц
              </div>
              <div
                class="c-input-container-period-link"
                :class="{
                  'c-input-container-period-link_active': period.range === 'custom',
                }"
                @click="period.range = 'custom'"
              >
                Произвольный период
              </div>
            </div>

            <div class="c-input-container-period-actions">
              <c-button
                class="c-input-container-period-action"
                size="small"
                label="Применить"
                :icon="{ icon: 'check', color: 'success' }"
                @click="periodSubmit"
              />

              <c-button
                class="c-input-container-period-action"
                size="small"
                label="Сбросить"
                :icon="{ icon: 'close', color: 'secondary' }"
                @click="periodReset"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { ru } from "vuejs-datepicker/dist/locale";
import moment from "moment";
import { TheMask, mask } from "vue-the-mask";

import Switcher from "@/components/controls/switcher";
import Button from "@/components/controls2/Button";

export default {
  name: "c-input",

  components: {
    "c-switcher": Switcher,
    "c-button": Button,
    TheMask,
    Datepicker,
  },

  directives: { mask },

  props: [
    "value",
    "type",
    "label",
    "floatingLabel",
    "size",
    "required",

    "placeholder",
    "placeholderStyle",
    "clearable",

    "icon",
    "iconRight",

    // Пропы селекта
    "items",
    "multiple",
    "searchable",

    "checkbox",
    "checkboxValue",

    "format",
    "editable",

    "mask",
    "withSpecialValue",

    "maxLength",
  ],

  data() {
    return {
      ru,

      textValue: !this.type || this.type === "text" || this.type === "textarea" ? this.value : "",
      selectValue: this.type === "select" ? this.value : null,

      container: false,

      selectSearch: "",
      selectSearchEmpty: false,

      selectItems: this.items,

      dateValue: this.type === "date" ? "" : this.type === "period" && "Прошлый месяц",
      selectedDate: null,

      focused: false,

      checkboxValueLocal: false,

      period: {
        range: "last-month",
        start: {
          input: "",
          selected: null,
          value: null,
          highlighted: null,
        },
        end: {
          input: "",
          selected: null,
          value: null,
          highlighted: null,
        },
        startMonth: null,
        endMonth: null,
        activeCalendar: "start",
      },
    };
  },

  watch: {
    "checkboxValue": function (value) {
      this.checkboxValueLocal = value;
    },

    "checkboxValueLocal": function (value) {
      this.$emit("checkboxChange", value);
    },

    "period.start.input": function (value) {
      if (value.length === 10) {
        const splitted = value.split(".");

        this.period.start.highlighted = moment(`${splitted[2]}-${splitted[1]}-${splitted[0]}`).toDate();
      }
    },

    "period.end.input": function (value) {
      if (value.length === 10) {
        const splitted = value.split(".");

        this.period.end.highlighted = moment(`${splitted[2]}-${splitted[1]}-${splitted[0]}`).toDate();
      }
    },

    "period.range": function (value) {
      this.setPeriod(value);
    },

    "items": function (value) {
      if (this.type === "select") {
        this.selectItems = value;
      }
    },

    "selectValue": function (value) {
      this.$emit("input", value);
    },

    "textValue": function (value) {
      if (this.type !== "date") {
        this.$emit("input", value);
      } else if (value.length === 10) {
        let date = value.split(".");
        date = `${date[2]}-${date[1]}-${date[0]}`;

        this.selectedDate = moment(date).toDate();
      }

      if (this.type === "textarea") {
        // this.$nextTick(() => {
        //   this.$refs.textarea.style.height = "auto";
        //   let newHeight = this.$refs.textarea.scrollHeight;
        //   this.$refs.textarea.style.height = `${newHeight}px`;
        // });
      }
    },

    "selectSearch": function (value) {
      if (this.type === "select" && this.items) {
        const items = this.items.map((item) => item);

        const searched = items.filter((i) => i.label.toLowerCase().includes(value.toLowerCase()));

        if (searched.length === 0) {
          this.selectSearchEmpty = true;
        } else {
          this.selectSearchEmpty = false;
        }

        this.selectItems = searched;
      }
    },

    "selectedDate": function (value) {
      this.container = false;
      this.dateValue = moment(value).format(this.format);

      if (this.editable) {
        this.textValue = moment(value).format("DD.MM.YYYY");
      }
    },

    "value": function (value) {
      if (this.type === "select") {
        this.selectValue = value;
      } else if (this.type === "period") {
        this.period.range = "custom";

        this.period.start.input = moment(value.start).format("DD.MM.YYYY");
        this.period.end.input = moment(value.end).format("DD.MM.YYYY");

        this.period.start.highlighted = value.start;
        this.period.end.highlighted = value.end;

        this.dateValue = `${this.period.start.input} - ${this.period.end.input}`;
      } else if (this.type === undefined && this.type === "text") {
        if (value === null) {
          this.textValue = "";
        } else {
          this.textValue = value;
        }
      }

      if (!value) {
        this.textValue = "";
      }

      if (this.type === "textarea") {
        this.$refs.textarea.style.height = "auto";
        const newHeight = this.$refs.textarea.scrollHeight;
        this.$refs.textarea.style.height = `${newHeight}px`;
      }
    },
  },

  mounted() {
    if (this.type === "period") {
      this.setPeriod("last-month");

      const today = moment().startOf("month");

      this.period.startMonth = moment(today).subtract(1, "month").toDate();
      this.period.endMonth = moment(today).toDate();
    }
    if (this.type === "textarea") {
      this.setTextareaHeight();
    }
  },

  updated() {
    this.setTextareaHeight();
  },

  methods: {
    setTextareaHeight() {
      // this.$nextTick(() => {
      // this.$refs.textarea.style.height = "auto";
      // let newHeight = this.$refs.textarea.scrollHeight;
      // console.log(newHeight);
      // this.$refs.textarea.style.height = `${newHeight}px`;
      // });
    },
    onInput(e) {
      if (this.type === "textarea") {
        this.textValue = e.target.innerText;
      }
    },

    periodSubmit() {
      this.container = false;
      const period = {
        start: null,
        end: null,
      };

      if (this.period.range === "today") {
        this.dateValue = "Сегодня";

        period.start = moment(this.period.end.selected).toDate();
        period.end = moment(this.period.end.selected).toDate();
      } else if (this.period.range === "yesterday") {
        this.dateValue = "Вчера";

        period.start = moment(this.period.end.selected).toDate();
        period.end = moment(this.period.end.selected).toDate();
      } else {
        if (this.period.range === "begin-week") {
          this.dateValue = "C начала недели";
        } else if (this.period.range === "past-week") {
          this.dateValue = "Прошлая неделя";
        } else if (this.period.range === "last-7-days") {
          this.dateValue = "Последние 7 дней";
        } else if (this.period.range === "current-month") {
          this.dateValue = "Текущий месяц";
        } else if (this.period.range === "past-month") {
          this.dateValue = "Прошлый месяц";
        } else if (this.period.range === "last-month") {
          this.dateValue = "Последний месяц";
        } else if (this.period.range === "custom") {
          this.dateValue = `${this.period.start.input} - ${this.period.end.input}`;
        }

        period.start = moment(this.period.start.highlighted).toDate();
        period.end = moment(this.period.end.highlighted).toDate();
      }

      this.$emit("input", period);
    },

    periodReset() {
      this.setPeriod("last-month");

      const today = moment().startOf("month");

      this.period.startMonth = moment(today).subtract(1, "month").toDate();
      this.period.endMonth = moment(today).toDate();

      this.period.start.input = "";
      this.period.end.input = "";

      this.dateValue = "Последний месяц";

      this.container = false;
    },

    periodSelectDay(date) {
      if (this.period.start.selected || this.period.end.selected) {
        if (moment(this.period.start.selected) > moment(date)) {
          this.period.start.highlighted = moment(date).toDate();
          this.period.end.highlighted = moment(this.period.start.selected).toDate();

          this.period.start.input = moment(date).format("DD.MM.YYYY");
          this.period.end.input = moment(this.period.start.selected).format("DD.MM.YYYY");
        } else if (moment(this.period.start.selected) < moment(date)) {
          this.period.start.highlighted = moment(this.period.start.selected).toDate();
          this.period.end.highlighted = moment(date).toDate();

          this.period.start.input = moment(this.period.start.selected).format("DD.MM.YYYY");
          this.period.end.input = moment(date).format("DD.MM.YYYY");
        } else if (moment(this.period.end.selected) > moment(date)) {
          this.period.start.highlighted = moment(date).toDate();
          this.period.end.highlighted = moment(this.period.end.selected).toDate();

          this.period.start.input = moment(date).format("DD.MM.YYYY");
          this.period.end.input = moment(this.period.end.selected).format("DD.MM.YYYY");
        } else if (moment(this.period.end.selected) < moment(date)) {
          this.period.start.highlighted = moment(this.period.end.selected).toDate();
          this.period.end.highlighted = moment(date).toDate();

          this.period.start.input = moment(this.period.end.selected).format("DD.MM.YYYY");
          this.period.end.input = moment(date).format("DD.MM.YYYY");
        } else {
          this.period.start.highlighted = null;
          this.period.end.highlighted = null;
        }
      }

      if (this.period.start.selected && this.period.end.selected) {
        this.period.start.highlighted = null;
        this.period.end.highlighted = null;
        this.period.start.selected = null;
        this.period.end.selected = null;
      }
    },

    changedStartMonth(value) {
      this.period.endMonth = moment(value).add(1, "month").toDate();
      this.$forceUpdate();
    },

    changedEndMonth(value) {
      this.period.startMonth = moment(value).subtract(1, "month").toDate();
      this.$forceUpdate();
    },

    setPeriod(value) {
      const today = moment().startOf("day");

      if (value === "today") {
        this.period.end.selected = moment(today).toDate();
        this.period.start.selected = null;

        this.period.start.highlighted = null;
        this.period.end.highlighted = null;
      } else if (value === "yesterday") {
        this.period.end.selected = moment(today).subtract(1, "day").toDate();
        this.period.start.selected = null;

        this.period.start.highlighted = null;
        this.period.end.highlighted = null;
      } else {
        if (value === "begin-week") {
          this.period.start.highlighted = moment(today).startOf("week").toDate();
          this.period.end.highlighted = moment(today).toDate();
        } else if (value === "past-week") {
          this.period.start.highlighted = moment(today).startOf("week").subtract(1, "week").toDate();
          this.period.end.highlighted = moment(today).endOf("week").subtract(1, "week").toDate();
        } else if (value === "last-7-days") {
          this.period.start.highlighted = moment(today).subtract(6, "day").toDate();
          this.period.end.highlighted = moment(today).toDate();
        } else if (value === "current-month") {
          this.period.start.highlighted = moment(today).startOf("month").toDate();
          this.period.end.highlighted = moment(today).toDate();
        } else if (value === "past-month") {
          this.period.start.highlighted = moment(today).subtract(1, "month").startOf("month").toDate();
          this.period.end.highlighted = moment(today).subtract(1, "month").endOf("month").toDate();
        } else if (value === "last-month") {
          this.period.start.highlighted = moment(today).subtract(1, "month").toDate();
          this.period.end.highlighted = moment(today).toDate();
        } else if (value === "custom") {
          this.period.start.highlighted = null;
          this.period.end.highlighted = null;
        }

        this.period.end.selected = null;
        this.period.start.selected = null;

        const month = moment().startOf("month");

        this.period.startMonth = moment(month).subtract(1, "month").toDate();
        this.period.endMonth = moment(month).toDate();
      }
    },

    selectItem(e, value) {
      if (this.multiple) {
        const selectValue = this.selectValue ? this.selectValue.map((item) => item) : [];

        const result = selectValue.findIndex((item) => item.value === value.value);

        if (result === -1) {
          selectValue.push(value);
        }

        this.selectValue = selectValue;
      } else {
        this.container = false;
        this.selectValue = value;
      }
    },

    openContainer() {
      this.container = true;
    },

    closeContainer() {
      this.container = false;
    },

    clear() {
      this.selectValue = null;
      this.dateValue = null;
      this.textValue = "";

      if (this.type === "textarea") {
        // this.$nextTick(() => {
        //   this.$refs.textarea.style.height = "auto";
        //   let newHeight = this.$refs.textarea.scrollHeight;
        //   this.$refs.textarea.style.height = `${newHeight}px`;
        // });
      }
    },

    dropItem(e, index) {
      this.selectValue.splice(index, 1);
    },

    isItemDisabled(item) {
      if (this.selectValue && this.multiple) {
        const result = this.selectValue.findIndex((i) => item.value === i.value);

        return result !== -1;
      }
      return false;
    },

    isArray(item) {
      return Array.isArray(item);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars";

.c-input {
  position: relative;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;

  &-required {
    display: flex;
    position: absolute;
    bottom: -20px;
    align-items: center;
    visibility: hidden;
    opacity: 0;
    transition: 0.2s;

    &_active {
      height: 14px;
      bottom: -22px;
      visibility: visible;
      opacity: 1;
    }

    &-text {
      font-size: 11px;
      color: $color-secondary;
      margin-left: 10px;
      margin-top: 1px;
    }
  }

  &-textarea {
    position: relative;
    width: 100%;
    padding: 0 0 13px 0;
    font-size: 15px;
    line-height: 108%;
    box-sizing: border-box;
    max-height: 200px;
    height: 30px;
    overflow-y: hidden;
    border: none;
    resize: none;

    @media screen and (max-height: 700px) {
      max-height: 100px;
    }

    &:focus {
      outline: none;

      & + .c-input-floating-label {
        font-size: 13px;
        //font-size: 80%;
        transform: translate3d(0, -100%, 0);
      }
    }

    &_floating_label_active + .c-input-floating-label {
      font-size: 13px;
      //font-size: 80%;
      transform: translate3d(0, -100%, 0);
    }
  }

  &-overlay {
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: $z-index-overlay;
    visibility: hidden;
    opacity: 0;
    transition: 0.2s;

    &_active {
      visibility: visible;
      opacity: 1;

      @media screen and (max-width: 700px) {
        &_select_mobile {
          background: rgba(0, 0, 0, 0.7);
          transition: 0.3s;
        }
      }
    }

    &_active + .c-input > .c-input-wrapper > .c-input-floating-label {
      font-size: 13px;
      //font-size: 80%;
      transform: translate3d(0, -100%, 0);
    }
  }

  &-label {
    font: {
      size: 12px;
    }
    line-height: 13px;
    margin-bottom: 10px;
    color: $color-label;
  }

  &-floating-label {
    position: absolute;
    overflow: hidden !important;
    max-width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    left: 0;
    font: 400 15px Fira Sans;
    pointer-events: none;
    line-height: 18px;
    color: $color-label;
    padding-bottom: 10px;
    transition: all 200ms;

    &-pr {
      padding-right: 15px;
    }

    &-active {
      font-size: 13px;
      //font-size: 80%;
      transform: translate3d(0, -100%, 0);
    }
  }

  &-floating-label-not-visible {
    font: {
      size: 12px;
    }
    visibility: hidden;
    line-height: 13px;
    margin-bottom: 10px;
    color: $color-label;
  }

  &-wrapper {
    width: 100%;
    display: flex;
  }

  &-value {
    width: calc(100% - 22px);
    height: 30px;
    font-size: 15px;
    padding: 0 0 13px 0;
    cursor: pointer;
    order: 2;

    &_size {
      &_small {
        font-size: 13px;
        padding-bottom: 9px;
        height: 30px;
        padding-top: 8px;
      }
    }

    &_placeholder {
      color: $color-label;
    }

    &_type {
      &_select {
        cursor: pointer;

        &_multiple {
          height: auto;
          min-height: 30px;
          padding-bottom: 0;
        }
      }
    }

    &-multiple {
      display: flex;
      margin-top: -5px;
      margin-left: -5px;
      flex-wrap: wrap;

      &-item {
        display: inline-flex;
        align-items: center;
        white-space: nowrap;
        height: 30px;
        border: 1px solid $color-border;
        font: {
          size: 12px;
          weight: 500;
        }
        border-radius: 15px;
        padding: 0 14px;
        margin-left: 5px;
        margin-bottom: 5px;
        transition: 0.2s;

        &-close {
          margin-left: 10px;
          // padding-top: 3px;

          svg {
            fill: $color-secondary;
            transition: 0.2s;
          }

          &:hover {
            svg {
              fill: $color-secondary-hover;
            }
          }
        }

        &:hover {
          border-color: $color-button-border-hover;
        }
      }
    }

    &-placeholder {
      padding-top: 5px;
      margin-left: 5px;
      color: $color-label;
    }

    &-date {
      color: $color-font;

      &_placeholder {
        color: $color-label;
      }
    }

    &-single {
      white-space: nowrap;
      max-width: calc(100% - 22px);
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &-input {
    border: none;
    width: 100%;
    font-size: 15px;
    padding: 0 0 10px 0;
    min-height: 30px;
    order: 2;

    &[disabled] {
      background: white;
    }

    &:focus {
      outline: none;

      & + .c-input-border {
        background: $color-primary;
      }
      & + .c-input-floating-label {
        font-size: 13px;
        //font-size: 80%;
        transform: translate3d(0, -100%, 0);
      }
    }

    &::placeholder {
      color: $color-label;
    }

    &_floating_label_active + .c-input-floating-label {
      font-size: 13px;
      //font-size: 80%;
      transform: translate3d(0, -100%, 0);
    }

    &_placeholder {
      &_italic {
        &::placeholder {
          font-style: italic;
        }
      }
    }

    &_size {
      &_small {
        font-size: 13px;
        padding-bottom: 9px;
        height: 30px;
        padding-top: 8px;
      }
    }
  }

  &-icon {
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    order: 1;
    margin-right: 10px;

    &-svg {
      &_stroke {
        stroke: $color-label;
        fill: none;
      }
    }

    &_right {
      order: 5;
      margin-left: 10px;
      margin-right: 0;
    }

    &_size {
      &_small {
        margin-top: 4px;
      }
    }
  }

  &-actions {
    display: flex;
    order: 3;
    align-items: baseline;

    &_size {
      &_small {
        // margin-bottom: 9px;
        margin-top: 4px;
      }
    }
  }

  &-action {
    margin-left: 10px;

    &_clear {
      margin-top: -3px;
      transition: 0.2s;
      opacity: 0;
      visibility: hidden;
      cursor: pointer;

      svg {
        fill: $color-secondary;
      }

      &_active {
        opacity: 1;
        visibility: visible;
      }
    }

    &_arrow {
      svg {
        fill: $color-label;
        transition: 0.2s;
      }

      &_active {
        svg {
          transform: rotate(180deg);
        }
      }
    }
  }

  &-checkbox {
    order: 4;
  }

  &-border {
    height: 1px;
    width: 100%;
    position: absolute;
    background: $color-control-border;
    bottom: 0;
    transition: 0.2s;

    &_active {
      background: $color-primary;
    }

    &_required {
      background: $color-pink;
    }
  }

  &-container {
    position: absolute;
    background: #ffffff;
    box-shadow: $box-shadow-container;
    border-radius: $border-radius-container;
    z-index: $z-index-container;
    top: 50px;
    width: 100%;
    box-sizing: border-box;
    transition: 0.2s;
    opacity: 0;
    visibility: hidden;
    padding: 5px 10px 10px 10px;

    @media screen and (max-width: 700px) {
      position: fixed;
      display: flex;
      flex-direction: column;
      width: calc(100% - 20px);
      height: calc(100% - 30px);
      padding: 5px 15px 20px 15px;
      transition: none;
    }

    &_nolabel {
      top: 27px;

      &.c-input-container_active {
        top: 37px;
      }
    }

    &-close {
      display: none;
      position: absolute;
      right: 10px;

      @media (max-width: 700px) {
        display: block;
      }
    }

    &_active {
      top: 60px;
      opacity: 1;
      visibility: visible;

      @media screen and (max-width: 700px) {
        transform: translate(-50%, -50%);
        top: 50%;
        left: 50%;
      }
    }

    &_date {
      width: auto;
    }

    &_period {
      width: auto;

      @media (max-width: 700px) {
        position: absolute;
        left: -10px;
        top: 60px;
        border-radius: 0;
        width: calc(100vw - 20px);
      }
    }

    &-period {
      padding: 20px 15px 15px;
      display: flex;

      @media (max-width: 700px) {
        flex-direction: column-reverse;
        padding: 15px 10px 10px;
      }

      &-switcher {
        display: none;

        @media (max-width: 700px) {
          display: flex;
          margin: 0 -20px;
          border-top: 1px solid $color-border-light;
          padding: 0 20px;
        }

        &-item {
          width: 100%;
          display: flex;
          align-items: center;
          font: {
            size: 13px;
            weight: 500;
          }
          color: $color-primary;

          span {
            position: relative;
            display: inline-block;
            line-height: 40px;

            &:before {
              position: absolute;
              content: "";
              height: 4px;
              width: 100%;
              background: $color-secondary;
              top: 40px;
              opacity: 0;
              visibility: hidden;
              transition: 0.2s;
            }
          }

          &_active {
            color: $color-font;

            span {
              &:before {
                opacity: 1;
                visibility: visible;
              }
            }
          }
        }
      }

      &-calendars {
        display: flex;

        @media (max-width: 700px) {
          margin: 0 -20px;
          padding: 20px;
          position: relative;
          height: 330px;

          &:before {
            content: "";
            position: absolute;
            width: 100%;
            height: 12px;
            background: linear-gradient(0, #8e9cbb 0%, #ffffff 100%);
            opacity: 0.2;
            top: -12px;
            left: 0;
          }
        }

        &_disabled {
          pointer-events: none;
        }

        &-item {
          &:first-child {
            margin-right: 23px;
          }

          @media (max-width: 700px) {
            position: absolute;
            opacity: 0;
            visibility: hidden;
            transition: 0.2s;
            width: calc(100% - 40px);

            &:first-child {
              margin-right: 0;
            }

            &_active {
              opacity: 1;
              visibility: visible;
            }
          }
        }
      }

      &-calendar {
        @media (max-width: 700px) {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
      }

      &-input {
        width: 100%;
        height: 30px;
        font-size: 15px;
        padding: 0 0 13px 0;
        border: none;

        &[disabled] {
          background: white;
        }

        &::placeholder {
          color: $color-label;
        }

        &:focus {
          outline: none;
        }

        &-wrapper {
          display: flex;
          width: 100%;
          border-bottom: 1px solid $color-border;
        }

        &-icon {
          margin-top: -1px;
        }
      }

      &-right {
        margin-left: 25px;

        @media (max-width: 700px) {
          margin-left: 0;
        }
      }

      &-links {
        margin-top: -12px;

        @media (max-width: 700px) {
          height: 150px;
          display: flex;
          flex-direction: column;
          flex-wrap: wrap;
          margin-bottom: 10px;
        }
      }

      &-link {
        line-height: 30px;
        font: {
          size: 13px;
          weight: 500;
        }
        color: $color-primary;
        cursor: pointer;
        transition: 0.2s;

        @media (max-width: 700px) {
          width: 50%;
          line-height: 16px;
          padding: 7px 0;
        }

        &_active {
          color: $color-label;
        }
      }

      &-actions {
        display: flex;
        margin-top: 10px;

        @media (max-width: 700px) {
          display: none;
        }

        &-mobile {
          display: none;

          @media (max-width: 700px) {
            display: flex;
            margin: 0 -20px -20px;
            box-shadow: 0px 0px 15px rgba(8, 20, 45, 0.3);
          }

          &-item {
            width: 100%;
            line-height: 38px;
            text-align: center;
            font: {
              size: 15px;
              weight: 500;
            }

            &:last-child {
              background: $color-primary;
              color: white;
            }
          }
        }
      }

      &-action {
        &:first-child {
          margin-right: 10px;
        }
      }
    }

    &_size {
      &_small {
        top: 20px;

        &.c-input-container {
          &_active {
            top: 30px;
          }

          .c-input-container-list {
            &-item {
              min-height: 24px;

              &-label {
                font-size: 13px;
              }
            }
          }
        }
      }
    }

    &-search {
      display: flex;
      align-items: center;
      position: relative;
      padding: 10px;
      margin-bottom: 13px;

      &-icon {
        margin-right: 10px;
        margin-top: -2px;

        svg {
          stroke: $color-label;
          fill: none;
        }
      }

      &-clear-icon {
        position: absolute;
        cursor: pointer;
        top: calc(50% - 1px);
        transform: translate(0, -50%);
        right: 10px;
      }

      &-input {
        border: none;
        height: 17px;
        margin-top: -3px;
        font-size: 13px;

        &:focus {
          outline: none;
        }

        &::placeholder {
          color: $color-label;
          font-style: italic;
        }
      }

      &-border {
        height: 1px;
        background: $color-border;
        width: calc(100% - 20px);
        position: absolute;
        bottom: 2px;
        transition: 0.2s;
      }

      &-with-special-value {
        margin-bottom: 5px;
      }
    }

    &-list {
      max-height: 200px;
      overflow-x: auto;

      @media screen and (max-width: 700px) {
        max-height: 100vh;
      }

      &::-webkit-scrollbar {
        width: 4px;
        height: 100%;
        background: white;
        cursor: pointer;
      }

      &::-webkit-scrollbar-thumb {
        background: $color-secondary;
        border-radius: 2px;
      }

      &::-webkit-scrollbar-track {
        background: $color-border-alt;
        width: 4px;
        border-radius: 2px;
      }

      &-item {
        font: {
          size: 15px;
        }
        min-height: 30px;
        padding: 0 14px;
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: 0.1s;
        border-radius: 15px;

        &:hover {
          background: $color-bg-hover;
        }

        &_empty {
          pointer-events: none;
        }

        &_selected {
          @media screen and (max-width: 700px) {
            background: $color-bg-hover;
          }
        }

        &_disabled {
          pointer-events: none;
          color: $color-label;
        }

        &_with_avatar {
          min-height: 44px;
          border-radius: 22px;
          padding-left: 10px;
        }

        &-avatar {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          margin-right: 10px;
          flex-shrink: 0;
        }

        &_special {
          position: relative;
          margin-bottom: 14px;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;

          & .c-input-container-list-item-border {
            bottom: -7px;
          }
        }

        &-border {
          position: absolute;
          bottom: -14px;
          left: 10px;
          width: calc(100% - 20px);
          height: 1px;
          background: $color-control-border;
        }

        &-label {
          max-width: 100%;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;

          &-bold {
            font-weight: 500;
          }
        }
      }

      &-mobile {
        display: none;

        &-buttons {
          display: none;
          margin: 0 0 0 10px;
        }

        @media screen and (max-width: 700px) {
          display: block !important;

          &-buttons {
            display: flex;
            position: relative;
            margin-top: auto;
            padding-top: 12px;
            align-items: flex-end;

            & .c-button_size_small {
              padding: 8px 20px;
            }

            &-cancel {
              margin-right: 5px;
            }

            &:before {
              content: "";
              position: absolute;
              pointer-events: none;
              width: 100%;
              height: 25px;
              top: -20px;
              left: 0;
              background: linear-gradient(rgba(255, 255, 255, 0) 0, #ffffff 100%);
            }
          }
        }

        & .c-input-container-list-item {
          padding: 0 10px;
        }

        & .c-input-container-list-item:hover {
          background: none;
        }
      }
    }
  }
}
</style>
